/**
 * The shell's non-component vocabulary: the nav model, the collapse breakpoint,
 * and the one preference the shell owns.
 *
 * It is a `.ts` module with no component in it for task 18's reason — a module
 * that exports a component may export nothing else, or
 * `react-refresh/only-export-components` fails `npm run verify`.
 */

import type { IconProps } from '@phosphor-icons/react';
import type { ComponentType } from 'react';

import type { NavGroupId } from '../navigation';

/**
 * Handoff, "Interactions & Behavior / Responsive": "Below 1100px the sidebar
 * collapses to icons." Exclusive: 1100px itself is wide enough.
 */
export const SIDEBAR_COLLAPSE_BREAKPOINT = 1100;

/**
 * Stable ids rather than `useId()`, because the skip link's `href` is a document
 * fragment an agent can type, bookmark and land on. React's generated ids
 * (`:r3:`) are opaque, change with the render tree, and put a colon inside a URL
 * fragment. There is exactly one shell per document.
 */
export const MAIN_CONTENT_ID = 'shell-main';
export const SIDEBAR_ID = 'shell-sidebar';

/**
 * Handoff, "State Management": `sidebarCollapsed` is global state persisted per
 * user. It is deliberately NOT part of `fsh.appearance`:
 *
 *  - `normaliseAppearance` rebuilds a three-field record, so a fourth field
 *    would be silently dropped on the next write;
 *  - the handoff's "Global switches" section says three attributes on `<html>`
 *    are the ONLY global modes, so this preference must never become a fourth.
 *
 * It also needs no no-flash boot snippet, unlike the theme: `<html>` paints its
 * background before React mounts, but the sidebar does not exist until React
 * mounts, so the first frame it has is already the right width.
 */
export const SIDEBAR_STORAGE_KEY = 'fsh.sidebarCollapsed';

/**
 * `null` means "the agent has never chosen", which is a different state from
 * "chose expanded" — it is what lets the viewport supply the default without
 * overriding a real decision. Anything that is not a boolean is treated as
 * never-chosen: a stored preference is untrusted text.
 */
export function readStoredSidebarCollapsed(): boolean | null {
  try {
    // Deliberately ONE path. An earlier version returned early on a missing key
    // and then re-checked the parsed type; mutation testing showed the two
    // masked each other perfectly — `JSON.parse('null')` fails the `typeof`
    // check anyway, so neither branch could be killed by any test. Two paths
    // that cannot be told apart are one path with a spare. (task 23 §6.5.5)
    const parsed: unknown = JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) ?? 'null');
    return typeof parsed === 'boolean' ? parsed : null;
  } catch {
    /* Private browsing, a blocked storage partition, or malformed JSON. */
    return null;
  }
}

export function writeStoredSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsed));
  } catch {
    /* Private browsing or quota: the preference simply does not survive. */
  }
}

/** Whether the viewport is too narrow to hold the expanded sidebar. */
export function isNarrowViewport(width: number = window.innerWidth): boolean {
  return width < SIDEBAR_COLLAPSE_BREAKPOINT;
}

/**
 * The viewport supplies the default; an explicit choice outranks it.
 *
 * The alternative — the viewport *forcing* collapse below 1100px — makes the
 * topbar's toggle inert at exactly the width where an agent is most likely to
 * reach for it, and a control that does nothing is worse than a narrow sidebar.
 */
export function sidebarIsCollapsed(preference: boolean | null, narrow: boolean): boolean {
  return preference ?? narrow;
}

/* ------------------------------------------------------------ the nav model */

/**
 * A count beside a nav item. Handoff, "App shell & navigation": "Queue counts in
 * the nav for lists an agent must drain — accent-300 numerals for informational
 * counts, red badge only when action is overdue."
 *
 * One object rather than three optional props, so a count without a name is
 * unrepresentable: a bare "7" beside "Requests" is a fact with no unit, and it
 * is the whole reason `CountBadge` takes a required `label`.
 */
export interface NavCount {
  count: number;
  /** The accessible name — a translated phrase naming what is counted. */
  label: string;
  /** Handoff: `alert` (red badge) only when action is overdue. */
  tone?: 'informational' | 'alert';
}

/** A second-level nav entry. It has no `children`, which is how the handoff's
 * "Nav depth stops at two levels" is enforced: a third level does not typecheck.
 */
export interface NavChildItem {
  id: string;
  label: string;
  path: string;
  count?: NavCount;
}

/**
 * `icon` is a component, not a rendered node, because the active item takes the
 * **fill** weight (handoff: "fill weight only for status dots and the active nav
 * item") and only the Sidebar knows which item is active. A pre-rendered
 * `ReactNode` could not be re-weighted without cloning it.
 */
export type NavIcon = ComponentType<IconProps>;

export interface NavItem extends NavChildItem {
  icon: NavIcon;
  children?: readonly NavChildItem[];
  /** Which sidebar section this belongs to. See `navigation.ts`'s `NAV_GROUPS`. */
  group: NavGroupId;
}

export interface BreadcrumbItem {
  label: string;
  /** Omitted on the last item: the page you are on is not a link to itself. */
  path?: string;
}

/**
 * Which nav sections the reader has FOLDED AWAY — never which are open.
 *
 * The stored value is the exception, not the state: every group is open
 * until someone closes one, so a first visit, a cleared profile and a
 * blocked storage partition all produce the same sidebar, and a group added
 * by a later milestone appears open rather than silently collapsed because
 * it was missing from a list written before it existed.
 *
 * Deliberately its own key rather than a field on `fsh.sidebarCollapsed`:
 * that value is a boolean the collapse toggle owns, and widening it to an
 * object would break every profile that already holds one.
 */
export const NAV_GROUPS_STORAGE_KEY = 'fsh.collapsedNavGroups';

export function readStoredCollapsedGroups(): readonly string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(NAV_GROUPS_STORAGE_KEY) ?? 'null');
    // A stored preference is untrusted text: anything that is not an array of
    // strings is treated as never-chosen rather than partially honoured.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === 'string');
  } catch {
    /* Private browsing, a blocked storage partition, or malformed JSON. */
    return [];
  }
}

export function writeStoredCollapsedGroups(ids: readonly string[]): void {
  try {
    localStorage.setItem(NAV_GROUPS_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* Private browsing or quota: the preference simply does not survive. */
  }
}
